/** *******************************************************************************************************************
 * Copyright (c) 2020 - Fundação CERTI
 * All rights reserved.
 ******************************************************************************************************************* */

import React, {
  useState, useCallback, useEffect, forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Toolbar,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  Grid,
  TablePagination,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FirstPage from '@material-ui/icons/FirstPage';
import LastPage from '@material-ui/icons/LastPage';
import Remove from '@material-ui/icons/Remove';
import SaveAlt from '@material-ui/icons/SaveAlt';
import SearchIcon from '@material-ui/icons/Search';
import ViewColumn from '@material-ui/icons/ViewColumn';

import MaterialTable from 'material-table';
import FilterListIcon from '@material-ui/icons/FilterList';
import useStyles from './styles';
import Search from './Search';
import FilterPanel from './FilterPanel';
import { rolesType } from '../../services/users';

const tableIcons = {
  // eslint-disable-next-line react/jsx-filename-extension
  Add: forwardRef((props, ref) => <AddIcon {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterListIcon {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <SearchIcon {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowUpward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const UserTable = ({
  handleLoadPage, usersData, onRowUpdate, page, isLoading, findByEmail, handleShowModal,
}) => {
  const [search, setSearch] = useState();
  const [showFilterPanel, setShowFilterPanel] = useState();
  const classes = useStyles();

  const handleClickSearch = useCallback(
    (event) => {
      event.preventDefault();
      findByEmail(search);
    },
    [findByEmail, search],
  );

  useEffect(
    () => {
      if (search === '') {
        handleLoadPage(null, 1);
      }
    },
    [handleLoadPage, search],
  );

  const handleClickRole = useCallback(
    (event) => {
      const { value } = event.currentTarget;
      handleShowModal(value);
    },
    [handleShowModal],
  );

  const handleFilterPanel = useCallback(() => {
    setShowFilterPanel((prev) => !prev);
  }, []);

  return (
    <>
      <Toolbar className={clsx(classes.toolbar, classes.highlight)}>
        <Grid justify="flex-start">
          <form onSubmit={handleClickSearch}>
            <Search value={search} onChange={setSearch} />
            <Button type="submit" onClick={handleClickSearch} disabled={!search || search.length < 3}>
              Buscar
            </Button>
          </form>
        </Grid>
        <Tooltip title="Filtrar lista">
          <IconButton aria-label="Filtrar lista" onClick={handleFilterPanel} disabled>
            <FilterListIcon />
          </IconButton>
        </Tooltip>

        {showFilterPanel && <FilterPanel onClickAway={handleFilterPanel} />}
      </Toolbar>
      {isLoading ? (
        <CircularProgress />
      ) : (
          <MaterialTable
            icons={tableIcons}
            style={{ boxShadow: 'none' }}
            columns={usersData.columns.map((column) => column.field !== 'roles'
              ? column
              : {
                ...column,
                // eslint-disable-next-line react/prop-types
                render: ({ id, roles }) => (
                  <Button className={classes.link} value={id} key={`roles-${id}`} onClick={handleClickRole}>
                    {/* eslint-disable-next-line react/prop-types */}
                    {roles?.map((role) => rolesType[role] && `${rolesType[role]} `)}
                  </Button>
                ),
              })}
            data={usersData.content}
            options={{
              paging: false,
              search: false,
              toolbar: false,
              showTitle: false,
              actionsColumnIndex: usersData.columns.length,
            }}
            localization={{
              header: {
                actions: 'Ações',
              },
              body: {
                editTooltip: 'Editar',
                editRow: {
                  cancelTooltip: 'Cancelar',
                  saveTooltip: 'Salvar',
                }
              },
            }}
            editable={{
              // onRowAdd,
              onRowUpdate,
              // onRowDelete,
            }}
          />
        )}
      {!isLoading && usersData?.content && !page.isSearching && (
        <TablePagination
          rowsPerPageOptions={[]}
          colSpan={3}
          count={usersData.numberOfUsers}
          rowsPerPage={15}
          page={page?.number}
          onChangePage={handleLoadPage}
          backIconButtonText={'Página anterior'}
          nextIconButtonText={'Próxima página'}
        />
      )}
    </>
  );
};

UserTable.propTypes = {
  usersData: PropTypes.shape({
    content: PropTypes.arrayOf(PropTypes.shape()),
    columns: PropTypes.arrayOf(PropTypes.shape()),
    numberOfUsers: PropTypes.number,
  }).isRequired,
  page: PropTypes.shape({
    isSearching: PropTypes.bool,
    number: PropTypes.number,
  }).isRequired,
  handleLoadPage: PropTypes.func.isRequired,
  onRowUpdate: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  findByEmail: PropTypes.func.isRequired,
  handleShowModal: PropTypes.func.isRequired,
};

export default UserTable;
